import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { CursorPage, Partner } from '@dru/types';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useQuery } from '../../lib/useQuery';

const REMEMBERED = 'dru.hospital.venue';

/**
 * Searching the empanelled network, server-side.
 *
 * Shared by every venue picker so they all search the same fields and page the
 * same way. Previously each screen fetched the first hundred partners and
 * rendered them as options, which works until the network outgrows a hundred
 * and then quietly stops offering the venue somebody is standing in.
 */
export function searchPartners(type?: string) {
  return async (search: string, cursor: string | null) => {
    const page = await api.get<CursorPage<Partner>>(
      `/partners?limit=20&q=${encodeURIComponent(search)}`
      + (type ? `&type=${type}` : '')
      + (cursor ? `&cursor=${cursor}` : ''),
    );
    return { items: page.items, nextCursor: page.nextCursor };
  };
}

/**
 * Which venue this session is working at.
 *
 * The queue, the check-in desk and the sample worklist are all one venue's
 * day, and the API asks for the partner explicitly. Remembering the choice
 * matters more than it looks: a coordinator who has to re-pick their own
 * hospital on every screen will eventually pick the wrong one, and a check-in
 * recorded against the wrong venue is a clinical record filed in the wrong
 * place.
 *
 * The remembered value is an id, so the record behind it is fetched once on
 * load — a picker that shows a uuid until you touch it is not a remembered
 * choice, it is a blank field with a value hidden in it.
 */
export function usePartner(type = 'HOSPITAL') {
  const { principal } = useAuth();
  // A venue account IS a venue. There is nothing to pick and nothing to
  // remember: the session is bound to one hospital or laboratory, the server
  // will refuse any other, and offering a picker would invite somebody to
  // choose a venue and be told no. The picker survives for the older
  // company-bound accounts, which can be empanelled at more than one.
  const own = principal?.partner ?? null;

  const remembered = typeof localStorage !== 'undefined'
    ? localStorage.getItem(REMEMBERED) : null;

  const [partner, setPartner] = useState<Partner | null>(null);

  // Every venue this account can pick from, fetched on load whether or not a
  // choice is remembered. It answers two questions a remembered id cannot: is
  // there anything to choose between — a coordinator empanelled at one hospital
  // should be told where they are, not asked — and is the remembered venue
  // still one of them. A venue that has been de-empanelled still restores by
  // id, so without this the picker reads correctly while every call it drives
  // comes back empty.
  const venues = useQuery<CursorPage<Partner> | null>(
    () => (own
      ? Promise.resolve(null)
      : api.get<CursorPage<Partner>>(`/partners?limit=50${type ? `&type=${type}` : ''}`)),
    [own, type],
  );

  const items = venues.data?.items;

  useEffect(() => {
    if (own || !items) return;
    setPartner((current) => {
      if (current && items.some((p) => p.id === current.id)) return current;
      // The remembered choice wins when it is still empanelled; otherwise the
      // first venue, so nobody lands on a blank wall they cannot leave.
      const next = items.find((p) => p.id === remembered) ?? items[0] ?? null;
      try {
        if (next) localStorage.setItem(REMEMBERED, next.id);
        else localStorage.removeItem(REMEMBERED);
      } catch {
        // A private window cannot remember. The picker still works for today.
      }
      return next;
    });
  }, [own, items, remembered]);

  const choose = useCallback((next: Partner | null) => {
    setPartner(next);
    try {
      if (next) localStorage.setItem(REMEMBERED, next.id);
      else localStorage.removeItem(REMEMBERED);
    } catch {
      // A private window cannot remember. The picker still works for today.
    }
  }, []);

  if (own) {
    return {
      partner: own as unknown as Partner,
      partnerId: own.id,
      setPartner: () => undefined,
      search: searchPartners(type),
      loading: false,
      error: null,
      /** The screen shows the venue's name instead of a picker. */
      fixed: true as const,
    };
  }

  return {
    partner,
    partnerId: partner?.id ?? '',
    setPartner: choose,
    search: searchPartners(type),
    loading: !partner && venues.loading,
    error: venues.error,
    /**
     * One venue is not a choice. The screen names it instead of offering a
     * dropdown that can only be re-picked. The cursor check keeps that honest
     * when the account is empanelled at more than one page of venues.
     */
    fixed: items ? items.length === 1 && !venues.data?.nextCursor : false,
  };
}

/**
 * Every venue this account can see, with "all" as a real option.
 *
 * The laboratory's work is not confined to one partner the way a check-in desk
 * is: a sample is drawn at the hospital that ran the encounter and resulted by
 * whichever bench holds it, so filtering the worklist to the laboratory's own
 * partner row hides exactly the tubes it is meant to be working. The default
 * is therefore no filter at all — the PARTNER scope on each endpoint already
 * decides what this account may see — and the picker narrows it when somebody
 * wants one venue's day.
 */
export function useVenueFilter() {
  const { principal } = useAuth();
  const own = principal?.partner ?? null;
  // The chosen venue rides in the address (`?venue=`), so a link from one
  // lab screen to the next — or a refresh — keeps it. Seeded from the URL;
  // the name is fetched once and falls back to a neutral label.
  const [params, setParams] = useSearchParams();
  const venueParam = own ? '' : params.get('venue') ?? '';
  const [partner, setPartnerState] = useState<Partner | null>(null);
  useEffect(() => {
    if (!venueParam || partner?.id === venueParam) return;
    let live = true;
    setPartnerState({ id: venueParam, name: 'Selected venue' } as Partner);
    api.get<Partner>(`/partners/${venueParam}`)
      .then((p) => { if (live) setPartnerState(p); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [venueParam]); // eslint-disable-line react-hooks/exhaustive-deps
  const setPartner = useCallback((p: Partner | null) => {
    setPartnerState(p);
    setParams((cur) => {
      const n = new URLSearchParams(cur);
      if (p) n.set('venue', p.id); else n.delete('venue');
      return n;
    }, { replace: true });
  }, [setParams]);

  // A venue account has one venue and the endpoints already narrow to it, so
  // there is no filter to offer — and no `partnerId` to send, because sending
  // one it does not hold is the one way to turn a working list into a 404.
  if (own) {
    return {
      partner: own as unknown as Partner,
      partnerId: own.id,
      setPartner: () => undefined,
      search: searchPartners(),
      query: '',
      fixed: true as const,
    };
  }

  return {
    partner,
    partnerId: partner?.id ?? '',
    setPartner,
    search: searchPartners(),
    /** Ready to append to a query string, empty when unfiltered. */
    query: partner ? `&partnerId=${partner.id}` : '',
    fixed: false as const,
  };
}

/**
 * Which console these screens are being read in.
 *
 * The queue and the check-in desk are one set of components rendered under
 * two prefixes — `/partner` for a venue's own login, `/hospital` for the
 * older company-bound accounts that still have one. A link written as a
 * literal would send half of their users into a console they cannot open, so
 * the prefix comes from where the reader already is.
 */
export function useVenuePrefix(): string {
  const { pathname } = useLocation();
  return pathname.startsWith('/partner') ? '/partner' : '/hospital';
}
